import { useState, useMemo, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Input } from '../../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { toast } from 'sonner';
import {
  Users,
  Award,
  Clock,
  AlertTriangle,
  UserCheck,
  Building,
  TrendingUp,
  MapPin,
  Mail,
  Phone,
  Search,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  GitBranch,
  ShieldAlert,
  Download,
  Calendar,
  Layers,
} from 'lucide-react';
import { getDownlineUserIds } from '../../data/mockData';
import { fetchUsers, fetchCases } from '../../api';

export default function TeamPerformance() {
  const { currentRole, currentUserId } = useApp();

  const [liveUsers, setLiveUsers] = useState([]);
  const [liveCases, setLiveCases] = useState([]);

  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      try {
        const [uRes, cRes] = await Promise.all([
          fetchUsers().catch(() => []),
          fetchCases().catch(() => [])
        ]);
        if (isMounted) {
          if (Array.isArray(uRes)) setLiveUsers(uRes);
          if (Array.isArray(cRes)) setLiveCases(cRes);
        }
      } catch (err) {
        console.error("Failed to load team performance data:", err);
      }
    }
    loadData();
    return () => { isMounted = false; };
  }, []);

  const rawCases = liveCases;

  // Selected node for visual highlight and filtering
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');

  // Mini Profile modal/drawer state
  const [selectedUserId, setSelectedUserId] = useState(null);

  // 1. Gather all downline users dynamically from mockHierarchy
  const downlineUsers = useMemo(() => {
    return liveUsers.map(u => {
      const userCases = rawCases.filter(c => c.assignedTo?.id === u.id || c.assignedTo_id === u.id);
      const active = userCases.filter(c => ['assigned', 'in_progress', 'qc_pending'].includes(c.status)).length;
      const completed = userCases.filter(c => ['completed', 'report_sent', 'qc_approved'].includes(c.status)).length;
      const total = userCases.length;
      const negative = userCases.filter(c => c.decisionStatus === 'negative' || c.status === 'qc_rejected').length;
      const rejectRate = total > 0 ? Math.round((negative / total) * 100) : 0;

      return {
        ...u,
        depth: u.role === 'admin' ? 0 : (u.role === 'supreme_manager' ? 1 : (u.role === 'manager' ? 2 : 3)),
        activeCases: active,
        completedCases: completed,
        totalCases: total,
        rejectRate,
        avgTat: 2.0,
        score: total > 0 ? 90 : 0,
        parentId: u.parent_id || null,
      };
    });
  }, [liveUsers, rawCases]);

  const supremeManager = downlineUsers.find(u => u.role === 'supreme_manager');
  const activeManager = downlineUsers.find(u => u.role === 'manager');
  const activeQC = downlineUsers.find(u => u.role === 'quality_checker');
  const fieldStaff = downlineUsers.filter(u => ['field_executive', 'vendor', 'business_partner'].includes(u.role));

  // Filtered list of users for grid table
  const filteredUsers = useMemo(() => {
    return downlineUsers.filter(u => {
      // If node is highlighted, show only that node or its subtree
      if (selectedNodeId && u.id !== selectedNodeId) {
        // If supreme manager is selected, show everything
        const isSM = downlineUsers.find(x => x.id === selectedNodeId)?.role === 'supreme_manager';
        // If manager selected, show manager and their supervisors + FEs
        const isMgr = downlineUsers.find(x => x.id === selectedNodeId)?.role === 'manager';
        const isQC = downlineUsers.find(x => x.id === selectedNodeId)?.role === 'quality_checker';

        if (isSM) return true;
        if (isMgr && u.role === 'supreme_manager') return false;
        if (isQC && !['quality_checker', 'field_executive', 'vendor'].includes(u.role)) return false;
        if (!isMgr && !isQC && u.id !== selectedNodeId) return false;
      }

      if (roleFilter !== 'All' && u.role !== roleFilter) return false;

      if (searchTerm && !u.name.toLowerCase().includes(searchTerm.toLowerCase()) && !u.email.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }

      return true;
    });
  }, [downlineUsers, selectedNodeId, roleFilter, searchTerm]);

  // Selected user profile for detail drawer
  const selectedUserProfile = useMemo(() => {
    return downlineUsers.find(u => u.id === selectedUserId);
  }, [downlineUsers, selectedUserId]);

  const handleTransmitWarning = (userName) => {
    toast.success(`Performance warnings and SLA notification sent to ${userName}.`);
  };

  const handleAuditSuccess = (userName) => {
    toast.success(`Audit rating checklist confirmed for ${userName}. Added +5 index bonus.`);
  };

  return (
    <div className="space-y-6 animate-fade-in text-slate-800 bg-slate-50 min-h-screen p-1" data-testid="team-performance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <GitBranch className="w-7 h-7 text-blue-600" />
            Downline Hierarchy & Performance Workspace
          </h1>
          <p className="text-sm text-slate-500 mt-1">Audit operational nodes, monitor field turnaround times (TAT), and review Quality QC rejection benchmarks.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              toast.success('Roster performance spreadsheet compiled successfully.');
            }}
            variant="outline"
            className="border-slate-200 text-slate-700 bg-white hover:bg-slate-50 font-semibold text-xs h-9"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Staff Roster
          </Button>
        </div>
      </div>

      {/* Hierarchy Node Map Dashboard */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-6">
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <GitBranch className="w-4 h-4 text-blue-600" />
            Interactive Vertical Downline Tree
          </h3>
          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
            Click any node card to focus scope
          </span>
        </div>

        {/* Dynamic Connected Node Tree Flow */}
        <div className="py-6 flex flex-col items-center justify-center space-y-8 select-none overflow-x-auto min-w-[600px]">
          
          {/* Level 1: Supreme Manager */}
          {supremeManager && (
            <div className="flex flex-col items-center">
              <button
                onClick={() => setSelectedNodeId(selectedNodeId === supremeManager.id ? null : supremeManager.id)}
                className={`relative flex items-center gap-3 p-4 rounded-xl border shadow-sm transition-all text-left bg-white w-64 group ${
                  selectedNodeId === supremeManager.id
                    ? 'border-blue-600 ring-2 ring-blue-500/10'
                    : 'border-slate-200 hover:border-blue-400 hover:shadow'
                }`}
              >
                <div className="absolute top-2 right-2 px-1 rounded bg-blue-100 text-[8px] uppercase tracking-wider font-extrabold text-blue-700">
                  Supreme
                </div>
                <Avatar className="w-10 h-10 border-2 border-blue-100 shrink-0">
                  <AvatarImage src={supremeManager.avatar} />
                  <AvatarFallback>PS</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <h4 className="font-bold text-xs text-slate-900 group-hover:text-blue-600 truncate">{supremeManager.name}</h4>
                  <p className="text-[10px] text-slate-400 font-bold uppercase mt-0.5">Supreme Manager</p>
                  <span className="text-[9px] text-slate-500 font-medium block mt-1">Scope: Ahmedabad & South</span>
                </div>
              </button>
              {/* Vertical connector line */}
              <div className="w-0.5 h-8 bg-slate-200" />
            </div>
          )}

          {/* Level 2: Manager */}
          {activeManager && (
            <div className="flex flex-col items-center">
              <button
                onClick={() => setSelectedNodeId(selectedNodeId === activeManager.id ? null : activeManager.id)}
                className={`relative flex items-center gap-3 p-4 rounded-xl border shadow-sm transition-all text-left bg-white w-64 group ${
                  selectedNodeId === activeManager.id
                    ? 'border-blue-600 ring-2 ring-blue-500/10'
                    : 'border-slate-200 hover:border-blue-400 hover:shadow'
                }`}
              >
                <div className="absolute top-2 right-2 px-1 rounded bg-indigo-100 text-[8px] uppercase tracking-wider font-extrabold text-indigo-700">
                  Manager
                </div>
                <Avatar className="w-10 h-10 border-2 border-indigo-100 shrink-0">
                  <AvatarImage src={activeManager.avatar} />
                  <AvatarFallback>AP</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <h4 className="font-bold text-xs text-slate-900 group-hover:text-blue-600 truncate">{activeManager.name}</h4>
                  <p className="text-[10px] text-slate-400 font-bold uppercase mt-0.5">Manager</p>
                  <span className="text-[9px] text-slate-500 font-medium block mt-1">{downlineUsers.length - 2} Downline staff</span>
                </div>
              </button>
              {/* Vertical connector line */}
              <div className="w-0.5 h-8 bg-slate-200" />
            </div>
          )}

          {/* Level 3: QC Supervisor */}
          {activeQC && (
            <div className="flex flex-col items-center">
              <button
                onClick={() => setSelectedNodeId(selectedNodeId === activeQC.id ? null : activeQC.id)}
                className={`relative flex items-center gap-3 p-4 rounded-xl border shadow-sm transition-all text-left bg-white w-64 group ${
                  selectedNodeId === activeQC.id
                    ? 'border-blue-600 ring-2 ring-blue-500/10'
                    : 'border-slate-200 hover:border-blue-400 hover:shadow'
                }`}
              >
                <div className="absolute top-2 right-2 px-1 rounded bg-emerald-100 text-[8px] uppercase tracking-wider font-extrabold text-emerald-700">
                  QC Lead
                </div>
                <Avatar className="w-10 h-10 border-2 border-emerald-100 shrink-0">
                  <AvatarImage src={activeQC.avatar} />
                  <AvatarFallback>SR</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <h4 className="font-bold text-xs text-slate-900 group-hover:text-blue-600 truncate">{activeQC.name}</h4>
                  <p className="text-[10px] text-slate-400 font-bold uppercase mt-0.5">QC Supervisor</p>
                  <span className="text-[9px] text-slate-500 font-medium block mt-1">Audit Score: 92% Acc</span>
                </div>
              </button>
              
              {/* Forked horizontal lines under QC lead */}
              <div className="w-[450px] h-0.5 bg-slate-200 mt-4 relative">
                {/* Horizontal center node line */}
                <div className="absolute left-1/2 -translate-x-1/2 -top-4 w-0.5 h-4 bg-slate-200" />
              </div>
            </div>
          )}

          {/* Level 4: FEs / Vendors */}
          <div className="flex items-start justify-center gap-6 pt-2">
            {fieldStaff.map((fe) => (
              <div key={fe.id} className="flex flex-col items-center">
                {/* Visual vertical segment connecting fork to card */}
                <div className="w-0.5 h-6 bg-slate-200 -mt-2 mb-2" />
                <button
                  onClick={() => setSelectedNodeId(selectedNodeId === fe.id ? null : fe.id)}
                  className={`relative flex flex-col p-4 rounded-xl border shadow-xs bg-white w-40 text-center transition-all group ${
                    selectedNodeId === fe.id
                      ? 'border-blue-600 ring-2 ring-blue-500/10'
                      : 'border-slate-200 hover:border-blue-400 hover:shadow-sm'
                  }`}
                >
                  <Avatar className="w-9 h-9 mx-auto border border-slate-100 shrink-0 mb-2">
                    <AvatarImage src={fe.avatar} />
                    <AvatarFallback>{fe.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <h4 className="font-bold text-[11px] text-slate-900 group-hover:text-blue-600 truncate w-full">{fe.name}</h4>
                  <span className="text-[8px] font-bold text-slate-400 uppercase mt-0.5 truncate block">
                    {fe.role === 'field_executive' ? 'Field Executive' : 'Vendor FE'}
                  </span>
                  
                  <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t border-slate-100 text-[10px] font-mono text-slate-600 font-bold">
                    <span className="text-blue-600">{fe.activeCases} Active</span>
                    <span className="text-slate-400">|</span>
                    <span className="text-emerald-600">{fe.avgTat}d TAT</span>
                  </div>
                </button>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Downline Personnel Performance Grid */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm flex flex-col">
        {/* Table Filters Bar */}
        <div className="p-5 border-b border-slate-200 bg-slate-50/50 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            <h3 className="font-bold text-slate-900 text-sm">Personnel Statistics Matrix</h3>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search downline name..."
                className="pl-8 bg-white border-slate-200 text-xs h-9 w-48"
              />
            </div>

            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg p-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 h-9"
            >
              <option value="All">All Roles</option>
              <option value="supreme_manager">Supreme Manager</option>
              <option value="manager">Manager</option>
              <option value="quality_checker">QC Supervisor</option>
              <option value="field_executive">Field Executive</option>
              <option value="vendor">Vendor FE</option>
            </select>

            {selectedNodeId && (
              <Button
                onClick={() => setSelectedNodeId(null)}
                variant="ghost"
                size="sm"
                className="text-xs text-rose-500 hover:text-rose-600 font-semibold"
              >
                Clear Node Focus
              </Button>
            )}
          </div>
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                <th className="p-4">Personnel Profile</th>
                <th className="p-4">Assigned Role</th>
                <th className="p-4">Account Status</th>
                <th className="p-4">Active Leads</th>
                <th className="p-4">Resolved Clear</th>
                <th className="p-4">Rejection Rate (%)</th>
                <th className="p-4">Average TAT</th>
                <th className="p-4">Quality Score</th>
                <th className="p-4 text-right">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredUsers.map((u) => {
                const isActive = u.status === 'active';
                return (
                  <tr
                    key={u.id}
                    onClick={() => setSelectedUserId(u.id)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors group"
                  >
                    <td className="p-4 flex items-center gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={u.avatar} />
                        <AvatarFallback>{u.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <h4 className="font-bold text-slate-800 truncate group-hover:text-blue-600">{u.name}</h4>
                        <span className="text-[10px] text-slate-400 font-mono block mt-0.5">{u.email}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-[10px] font-extrabold uppercase text-slate-500">
                        {u.role === 'supreme_manager' ? 'Supreme SM' : (u.role === 'quality_checker' ? 'QC Supervisor' : u.role)}
                      </span>
                    </td>
                    <td className="p-4">
                      <Badge className={isActive ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-slate-100 text-slate-600 border-slate-200'}>
                        {isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="p-4 font-semibold text-amber-600">{u.activeCases} Cases</td>
                    <td className="p-4 font-semibold text-emerald-600">{u.completedCases} Cases</td>
                    <td className="p-4 font-mono font-semibold text-rose-500">
                      {u.rejectRate > 0 ? `${u.rejectRate}%` : '0%'}
                    </td>
                    <td className="p-4 font-mono font-bold text-slate-800">
                      {u.avgTat} Days
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        <Award className="w-3.5 h-3.5 text-blue-500" />
                        <span className="font-extrabold text-slate-800">{u.score}/100</span>
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform inline" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Downline Personnel Performance Drawer / Modal Detail */}
      {selectedUserId && selectedUserProfile && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-lg w-full border border-slate-200 space-y-6 animate-fade-in relative">
            <button
              onClick={() => setSelectedUserId(null)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-slate-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Profile Header */}
            <div className="flex items-center gap-4 border-b border-slate-100 pb-4">
              <Avatar className="w-14 h-14 border border-slate-200">
                <AvatarImage src={selectedUserProfile.avatar} />
                <AvatarFallback>{selectedUserProfile.name.slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <h3 className="font-extrabold text-slate-900 text-base">{selectedUserProfile.name}</h3>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mt-0.5">
                  {selectedUserProfile.role === 'supreme_manager' ? 'Supreme SM' : selectedUserProfile.role}
                </span>
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 mt-1.5">
                  Verified Downline Mapped
                </Badge>
              </div>
            </div>

            {/* Quick Contact Specs */}
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200/50">
                <Mail className="w-4 h-4 text-blue-500 shrink-0" />
                <span className="text-slate-600 truncate">{selectedUserProfile.email}</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200/50">
                <Phone className="w-4 h-4 text-blue-500 shrink-0" />
                <span className="text-slate-600 font-semibold">{selectedUserProfile.phone || '+91 98765 43210'}</span>
              </div>
            </div>

            {/* Detailed Performance Telemetry Grid */}
            <div className="space-y-3">
              <h4 className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider">Performance Telemetry</h4>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 border border-slate-200 rounded-lg bg-slate-50/50">
                  <span className="text-[9px] uppercase font-bold text-slate-400 block">SLA Compliance</span>
                  <span className="text-base font-extrabold text-emerald-600 mt-1 block">
                    {selectedUserProfile.score > 85 ? 'High (SLA Pass)' : 'Caution'}
                  </span>
                </div>
                <div className="p-3 border border-slate-200 rounded-lg bg-slate-50/50">
                  <span className="text-[9px] uppercase font-bold text-slate-400 block">Average TAT</span>
                  <span className="text-base font-extrabold text-slate-800 mt-1 block font-mono">
                    {selectedUserProfile.avgTat} Days
                  </span>
                </div>
                <div className="p-3 border border-slate-200 rounded-lg bg-slate-50/50">
                  <span className="text-[9px] uppercase font-bold text-slate-400 block">QC Declines</span>
                  <span className="text-base font-extrabold text-rose-500 mt-1 block font-mono">
                    {selectedUserProfile.rejectRate}%
                  </span>
                </div>
              </div>
            </div>

            {/* Verification Stats Summary */}
            <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl space-y-2">
              <h4 className="text-[10px] font-extrabold uppercase text-slate-500">Sub-staff Audit Index</h4>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-500 font-medium">Total Cases Processed</span>
                <span className="font-bold text-slate-800">{selectedUserProfile.totalCases} Leads</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-500 font-medium">Cases Pending Action</span>
                <span className="font-bold text-amber-600">{selectedUserProfile.activeCases} Active</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-500 font-medium">Account Join Date</span>
                <span className="font-bold text-slate-800">{selectedUserProfile.joinDate || '2023-01-15'}</span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 justify-end pt-2">
              <Button
                onClick={() => {
                  setSelectedUserId(null);
                  handleAuditSuccess(selectedUserProfile.name);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-9"
              >
                Approve Performance Audit
              </Button>
              {selectedUserProfile.role !== 'supreme_manager' && (
                <Button
                  onClick={() => {
                    setSelectedUserId(null);
                    handleTransmitWarning(selectedUserProfile.name);
                  }}
                  variant="outline"
                  className="border-slate-200 text-rose-600 hover:bg-rose-50 bg-white font-semibold text-xs h-9"
                >
                  Raise SLA Alert
                </Button>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

// Simple X SVG alternative because vaul / Radix sometimes needs close element explicitly
function X({ className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
  );
}
